import { createContext, useContext, useState } from 'react';

/* ─────────────────────────────────────────────
   Contexte unique de langue pour toute l'app
───────────────────────────────────────────── */
const TranslationContext = createContext(null);

/**
 * Provider à placer à la racine de l'app (dans App.jsx).
 */
export function TranslationProvider({ children, defaultLangue = 'fr' }) {
  const [langue, setLangue] = useState(defaultLangue);

  return (
    <TranslationContext.Provider value={{ langue, setLangue }}>
      {children}
    </TranslationContext.Provider>
  );
}

/**
 * Hook bas niveau — accès direct à { langue, setLangue }.
 * Utilisé par Navbar pour changer la langue.
 */
export function useTranslationContext() {
  const ctx = useContext(TranslationContext);
  if (!ctx) {
    throw new Error('useTranslationContext doit être utilisé dans <TranslationProvider>');
  }
  return ctx;
}

/**
 * Hook principal — retourne une fonction t(key) qui résout
 * les traductions depuis les JSON locaux du composant.
 *
 * - Supporte les clés imbriquées : t('modal.type')
 * - Retourne tableau/objet brut si la valeur n'est pas une string
 * - Retourne undefined (jamais la clé) si non trouvée, pour
 *   permettre les guards Array.isArray() en dehors
 *
 * @param {{ fr: object, en: object }} translations
 * @returns {(key: string) => any}
 */
export default function useAutoTranslate({ fr, en }) {
  const ctx = useContext(TranslationContext);
  const langue = ctx?.langue ?? 'fr';

  const translations = { fr, en };
  const data = translations[langue] ?? translations['fr'] ?? {};

  return function t(key) {
    return key.split('.').reduce((obj, k) => obj?.[k], data);
  };
}